<script lang="ts" setup>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { http } from '@/core/api';
import { Notyf } from 'notyf';
import { formInput, lightButtonStyle } from '@/assets/css/styles';
import { useField, useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as zod from 'zod';
import Head from '@/components/Header/Head.vue';
import LoadingSpinner from '@/components/icons/LoadingSpinner.vue';

const route = useRoute();
const router = useRouter();
const notyf = new Notyf();

const loading = ref(false);
const success = ref(false);

const validationSchema = toTypedSchema(
  zod
    .object({
      password: zod
        .string({
          required_error: 'Campo obrigatório',
        })
        .nonempty('Campo obrigatório')
        .min(8, 'Sua senha deve conter, ao menos, 8 caracteres'),
      passwordCheck: zod.string(),
    })
    .refine((data) => data.password === data.passwordCheck, {
      message: 'As senhas devem ser iguais',
      path: ['passwordCheck'],
    })
);
const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: password } = useField('password');
const { value: passwordCheck } = useField('passwordCheck');

const setNewPassword = handleSubmit(async (values) => {
  loading.value = true;
  try {
    await http.put(`/users/updatePassword/${route.params.token}`, {
      password: values.password,
    });

    success.value = true;
  } catch (err: any) {
    notyf.error(err.message);
    console.error(err);
  }
  loading.value = false;
});
</script>

<template>
  <div class="w-full h-screen overflow-auto flex flex-col gap-8 bg-[url('@/assets/img/bg-login.svg')] bg-cover">
    <Head />
    <main class="w-full flex flex-col justify-center items-center gap-20">
      <img src="@/assets/img/adv-login-logo.svg" alt="login logo" class="h-28 w-auto hidden z-10 lg:block" />
      <section
        v-show="!success"
        class="max-w-5xl w-full rounded-3xl p-6 lg:p-12 flex flex-col justify-center bg-white items-center gap-14"
      >
        <h3 class="text-adv-blue-800 font-bold text-2xl tracking-wide text-center">Criar nova senha</h3>
        <p class="text-adv-gray-400 text-lg text-center">Insira e confirme sua nova senha.</p>
        <form @submit.prevent="setNewPassword" class="contents">
          <div class="w-full sm:w-4/5">
            <p class="text-[#A8A8A8] font-bold tracking-widest text-xl mb-4">Nova senha</p>
            <input
              type="password"
              v-model="password"
              :class="formInput + `${errors.password ? 'border-2 border-rose-500' : ''}`"
            />
            <span class="mt-1 text-xs text-rose-500">{{ errors.password }}</span>
          </div>
          <div class="w-full sm:w-4/5">
            <p class="text-[#A8A8A8] font-bold tracking-widest text-xl mb-4">Confirmar nova senha</p>
            <input
              type="password"
              v-model="passwordCheck"
              :class="formInput + `${errors.passwordCheck ? 'border-2 border-rose-500' : ''}`"
            />
            <span class="mt-1 text-xs text-rose-500">{{ errors.passwordCheck }}</span>
          </div>
          <button type="submit" :class="lightButtonStyle" class="w-full flex justify-center items-center">
            <LoadingSpinner v-show="loading" />
            <p v-show="!loading">ENVIAR</p>
          </button>
        </form>
      </section>
      <section
        v-show="success"
        class="max-w-5xl w-full rounded-3xl p-6 lg:p-24 flex flex-col justify-center bg-white items-center gap-14"
      >
        <h3 class="text-adv-blue-800 font-bold text-2xl tracking-wide text-center">Senha alterada com sucesso!</h3>
        <button
          type="button"
          :class="lightButtonStyle"
          class="flex justify-center items-center"
          @click="router.push('/login')"
        >
          <p>Entrar</p>
        </button>
      </section>
    </main>
  </div>
</template>
